// resources/js/src/components/community/components/profile/ProfileNotAvailable.tsx
import React from "react";
import { Icon } from "@iconify/react";
import type { ProfileState } from "../../../../../services/profile.api";
import { useNavigate } from "react-router-dom";

/*
|--------------------------------------------------------------------------
| ProfileNotAvailable
|--------------------------------------------------------------------------
| شاشة موحّدة للحالات اللي مش بنعرض فيها بروفايل:
| - not_found       → "User not found"
| - banned          → "Account suspended"
| - blocked_by_me   → "You blocked this user"
| - blocked_by_them → "This profile is not available"
|--------------------------------------------------------------------------
*/

type Props = {
  state: ProfileState;
};

const CONFIG: Record<
  string,
  {
    icon: string;
    iconColor: string;
    bgColor: string;
    title: string;
    description: string;
    actionLabel?: string;
    actionHref?: string;
  }
> = {
  not_found: {
    icon: "mdi:account-search-outline",
    iconColor: "text-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    title: "User not found",
    description: "This account may have been deleted or never existed.",
  },
  banned: {
    icon: "mdi:account-cancel-outline",
    iconColor: "text-red-500",
    bgColor: "bg-red-50 dark:bg-red-900/20",
    title: "Account suspended",
    description: "This account has been suspended and is no longer available.",
  },
  blocked_by_me: {
    icon: "mdi:block-helper",
    iconColor: "text-orange-500",
    bgColor: "bg-orange-50 dark:bg-orange-900/20",
    title: "You blocked this user",
    description: "You can unblock this user from your blocked list.",
    actionLabel: "Go to Blocked List",
    actionHref: "/settings/blocked",
  },
  blocked_by_them: {
    icon: "mdi:lock-outline",
    iconColor: "text-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
    title: "Profile not available",
    description: "This profile is not available to you.",
  },
};

export const ProfileNotAvailable: React.FC<Props> = ({ state }) => {
  const navigate = useNavigate();
  const cfg = CONFIG[state] ?? CONFIG.not_found;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50 dark:bg-deepSlate">
      <div className="relative max-w-md w-full text-center">
        {/* ===== Icon ===== */}
        <div
          className={`mx-auto mb-6 w-24 h-24 rounded-full flex items-center justify-center ${cfg.bgColor} shadow-lg`}
        >
          <Icon
            icon={cfg.icon}
            className={`text-5xl ${cfg.iconColor}`}
          />
        </div>

        {/* ===== Title ===== */}
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-3">
          {cfg.title}
        </h1>

        {/* ===== Description ===== */}
        <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
          {cfg.description}
        </p>

        {/* ===== Actions ===== */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {cfg.actionHref && cfg.actionLabel && (
            <button
              onClick={() => navigate(cfg.actionHref!)}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/40 hover:from-primary-700 hover:to-primary-600 transition-all duration-200 active:scale-[0.97]"
            >
              <Icon icon="mdi:cog-outline" className="text-lg" />
              {cfg.actionLabel}
            </button>
          )}

          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 transition-all duration-200 active:scale-[0.97]"
          >
            <Icon icon="mdi:home-outline" className="text-lg" />
            Go Home
          </button>
        </div>

        {/* ===== Decorative gradient ===== */}
        <div className="absolute -top-10 -left-10 w-32 h-32 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-primary-500/10 rounded-full blur-3xl pointer-events-none" />
      </div>
    </div>
  );
};

export default ProfileNotAvailable;